import React, { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { parseMarkdownEvent } from "@origin-digital/event-dispatcher";
import { Box, Heading, Text, Stack, TextLink } from "@origin-digital/ods-core";
import { FdvBanner } from "../../FdvBanner";

import { ArticleBodyStyles } from "./ArticleBody.styles";
import { ATTR_NAME, replaceHtml, scrollToAnchor } from "./helper";

interface IArticleBody {
  title: string;
  body: string;
  isFdvArticle?: boolean;
}

export const ArticleBody = ({ body, title, isFdvArticle }: IArticleBody) => {
  const articleRef = useRef(null);
  const routerLocation = useLocation();

  useEffect(() => {
    scrollToAnchor(routerLocation);
  }, [routerLocation]);

  useEffect(() => {
    const cleanUp: (() => void)[] = [];
    if (articleRef.current && body) {
      // eslint-disable-next-line react/no-find-dom-node
      const dom = articleRef.current as HTMLElement;
      const elmsList = dom?.querySelectorAll(`a[${ATTR_NAME}]`);
      const elms: Element[] = [].slice.call(elmsList);
      elms.forEach((elm) => {
        const navToMd = elm.getAttribute(ATTR_NAME);
        if (navToMd) {
          const dispatchEvent = parseMarkdownEvent(navToMd, {
            logErrors: true,
          })?.onClick;
          if (dispatchEvent) {
            const cbFn = (e: Event) => {
              e.preventDefault();
              dispatchEvent();
            };
            elm.addEventListener("click", cbFn);
            cleanUp.push(() => elm.removeEventListener("click", cbFn));
          }
        }
      });
    }

    return () => {
      cleanUp.forEach((fn) => fn());
    };
  }, [body]); // needs to re-run on new body

  return (
    <>
      <Stack space="xlarge">
        <Heading component="h2" variant="h2">
          <TextLink
            id="heading"
            href="#heading"
            domProps={{ style: { textDecoration: "none" } }}
          >
            {title}
          </TextLink>
        </Heading>
      </Stack>

      {isFdvArticle && (
        <Box
          borderRadius="large"
          overflow="hidden"
          marginY="xxlarge"
          marginBottom="xxlarge"
        >
          <FdvBanner />
        </Box>
      )}

      <Text>
        <ArticleBodyStyles
          ref={articleRef}
          dangerouslySetInnerHTML={{ __html: replaceHtml(body) }}
        />
      </Text>
    </>
  );
};
